import React, { useState, useEffect } from 'react';
import { useAuth } from '../../components/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { FaEnvelope, FaLock, FaCheckCircle, FaChevronRight, FaArrowLeft } from 'react-icons/fa';

const ForgotPassword = () => {
  const { sendResetCode, verifyResetCode, resetPasswordWithCode } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [step, setStep] = useState(1); // 1: Email, 2: Code, 3: New Password
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  // 60초 타이머 로직
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timeLeft]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const result = await sendResetCode(email);
    if (result.success) {
      setStep(2);
      setTimeLeft(60);
      setSuccess('인증코드를 이메일로 발송했습니다.');
    } else {
      setError(result.error);
    }
    setIsLoading(false);
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError('인증코드 6자리를 입력해주세요.');
      return;
    }
    
    setIsLoading(true);
    setError('');
    const result = await verifyResetCode(email, code);
    
    if (result.success) {
      setStep(3);
      setSuccess('인증에 성공했습니다. 새로운 비밀번호를 입력해주세요.');
    } else {
      setError(result.error);
    }
    setIsLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    if (newPassword.length < 6) {
      setError('비밀번호는 최소 6자 이상이어야 합니다.');
      return;
    }

    setIsLoading(true);
    setError('');

    const result = await resetPasswordWithCode(email, code, newPassword);
    if (result.success) {
      setSuccess('비밀번호가 성공적으로 변경되었습니다. 잠시 후 로그인 페이지로 이동합니다.');
      setTimeout(() => navigate('/login'), 2000);
    } else {
      setError(result.error);
    }
    setIsLoading(false);
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <form className="space-y-6" onSubmit={handleSendCode}>
            <div>
              <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700">가입한 이메일 주소</label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaEnvelope className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  id="reset-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2.5 sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="name@example.com"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none transition-colors"
            >
              {isLoading ? '발송 중...' : (
                <>인증코드 받기 <FaChevronRight className="ml-2 mt-1 h-3 w-3" /></>
              )}
            </button>
          </form>
        );
      case 2:
        return (
          <form className="space-y-6" onSubmit={handleVerifyCode}>
            <div>
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700">인증코드 입력</label>
                <span className={`text-xs font-bold ${timeLeft > 0 ? 'text-red-500' : 'text-gray-400'}`}>
                  남은 시간: {timeLeft}초
                </span>
              </div>
              <input
                type="text"
                required
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="mt-1 block w-full text-center text-2xl tracking-[12px] font-black py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                placeholder="000000"
              />
              {timeLeft === 0 && (
                <button 
                  type="button"
                  onClick={handleSendCode}
                  className="mt-2 text-xs text-blue-600 hover:underline"
                >
                  인증코드 재발송
                </button>
              )}
            </div>
            <button
              type="submit"
              disabled={timeLeft === 0 || isLoading}
              className={`w-full py-2.5 px-4 rounded-lg shadow-sm text-sm font-bold text-white transition-colors ${timeLeft > 0 && !isLoading ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'}`}
            >
              {isLoading ? '확인 중...' : '다음 단계'}
            </button>
          </form>
        );
      case 3:
        return (
          <form className="space-y-6" onSubmit={handleResetPassword}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">새 비밀번호</label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2.5 sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="6자 이상 입력"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">새 비밀번호 확인</label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2.5 sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="비밀번호 재입력"
                  />
                </div>
              </div>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 px-4 rounded-lg shadow-sm text-sm font-bold text-white bg-green-600 hover:bg-green-700 transition-colors"
            >
              {isLoading ? '변경 중...' : '비밀번호 변경 완료'}
            </button>
          </form>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pt-20 sm:pt-32 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/login" className="inline-flex items-center text-sm text-gray-500 hover:text-blue-600 mb-6 transition-colors">
          <FaArrowLeft className="mr-2 h-3 w-3" /> 로그인으로 돌아가기
        </Link>
        <h2 className="text-3xl font-extrabold text-gray-900">비밀번호 찾기</h2>
        <p className="mt-2 text-sm text-gray-600">이메일 인증을 통해 비밀번호를 재설정하세요.</p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 border border-gray-100">
          <div className="mb-8">
            <div className="flex items-center justify-between relative">
              <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gray-200 -z-0"></div>
              {[1, 2, 3].map((s) => (
                <div 
                  key={s} 
                  className={`relative z-10 w-8 h-8 flex items-center justify-center rounded-full text-xs font-bold transition-all ${
                    step >= s ? 'bg-blue-600 text-white shadow-lg' : 'bg-white border-2 border-gray-200 text-gray-400'
                  }`}
                >
                  {step > s ? <FaCheckCircle /> : s}
                </div>
              ))}
            </div>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg">{error}</div>}
          {success && <div className="mb-4 p-3 bg-green-50 text-green-600 text-sm rounded-lg flex items-center">
            <FaCheckCircle className="mr-2" /> {success}
          </div>}

          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
